import React, { ReactElement, ReactNode } from "react";
import styles from "./UtilityPageLayout.module.scss";
import Button from "../../../../common/components/xepp/button/Button";
import InfoTooltip from "../../../../common/components/info-tooltip/InfoTooltip";
import FeatureLockedState from "../../../../common/components/feature-locked-state/FeatureLockedState";
import { FeatureFlagName } from "../../../../tenant/features/featureFlags";
import { useFeatureFlags } from "../../../../tenant/features/useFeatureFlag";
import { ADMIN_FEATURE_INFO } from "../../adminFeatureCatalogue";

interface UtilityPageLayoutLayoutProps {
	title: string;
	children: ReactNode;
	customButton?: false | ReactElement<typeof Button>;
	middleComponent?: false | ReactElement;
	/**
	 * The admin feature flag behind this page. Adds the feature's description as a
	 * tooltip next to the title and, while the flag is off for the utility, shows the
	 * locked state instead of the content — the page stays reachable, the content does not.
	 */
	feature?: FeatureFlagName;
}

const UtilityPageLayout: React.FC<UtilityPageLayoutLayoutProps> = ({
	title,
	children,
	customButton,
	middleComponent,
	feature,
}) => {
	const { isEnabled, isLoading } = useFeatureFlags();
	const info = feature ? ADMIN_FEATURE_INFO[feature] : undefined;
	const locked = feature !== undefined && !isEnabled(feature);

	const renderContent = () => {
		// Flags all read false until the SDK answers; don't paint the lock over a
		// feature that is about to turn out enabled.
		if (feature && isLoading) return null;
		if (locked) return <FeatureLockedState featureName={info?.title ?? title} />;
		return children;
	};

	return (
		<div className={styles.container}>
			{title && (
				<div className={styles.titleContainer}>
					<div className={styles.titleGroup}>
						<h1 className={styles.title}>{title}</h1>
						{info && <InfoTooltip title={info.title} description={info.description} />}
					</div>
					{!locked && middleComponent}
					{!locked && customButton}
				</div>
			)}
			<div>{renderContent()}</div>
		</div>
	);
};

export default UtilityPageLayout;
